import { useLingui } from '@lingui/react/macro'
import { TooltipWrapper } from 'components/TooltipWrapper'
import { Button } from 'designSystem/components/Button'
import { GlassButtonWell } from 'features/knowledgeDocumentViewer/components/viewer/shared/glassSurface'
import {
  Eye,
  EyeOff,
  Map,
  Maximize2,
  Minimize2,
  Minus,
  Plus,
  Scan,
} from 'lucide-react'
import React from 'react'
import styled from 'styled-components'

const ZoomControlsContainer = styled.div`
  position: absolute;
  right: calc(1rem + env(safe-area-inset-right, 0px));
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  touch-action: none;
`

interface ZoomControlsProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  showResetZoom?: boolean
  onToggleFullscreen?: () => void
  isFullscreen?: boolean
  onToggleMinimap?: () => void
  isMinimapVisible?: boolean
  onTogglePhotoCounts?: () => void
  arePhotoCountsVisible?: boolean
  disabled?: boolean
  zoomOutDisabled?: boolean
  zoomInDisabled?: boolean
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  onZoomIn,
  onZoomOut,
  onResetZoom,
  showResetZoom = true,
  onToggleFullscreen,
  isFullscreen = false,
  onToggleMinimap,
  isMinimapVisible = false,
  onTogglePhotoCounts,
  arePhotoCountsVisible = false,
  disabled = false,
  zoomOutDisabled = false,
  zoomInDisabled = false,
}) => {
  const { t } = useLingui()
  const minimapLabel = isMinimapVisible ? t`Hide Minimap` : t`Show Minimap`
  const fullscreenLabel = isFullscreen ? t`Exit fullscreen` : t`Fullscreen`
  const photoCountsLabel = arePhotoCountsVisible
    ? t`Hide photo counts`
    : t`Show photo counts`
  return (
    <ZoomControlsContainer>
      <GlassButtonWell>
        <Button
          type='button'
          variant='ghost'
          size='icon-lg'
          disabled={disabled || zoomInDisabled}
          onClick={onZoomIn}
          title={t`Zoom In`}
          aria-label={t`Zoom In`}>
          <Plus />
        </Button>
      </GlassButtonWell>
      <GlassButtonWell>
        <Button
          type='button'
          variant='ghost'
          size='icon-lg'
          disabled={disabled || zoomOutDisabled}
          onClick={onZoomOut}
          title={t`Zoom Out`}
          aria-label={t`Zoom Out`}>
          <Minus />
        </Button>
      </GlassButtonWell>
      {showResetZoom ? (
        <GlassButtonWell>
          <Button
            type='button'
            variant='ghost'
            size='icon-lg'
            disabled={disabled}
            onClick={onResetZoom}
            title={t`Reset Zoom`}
            aria-label={t`Reset Zoom`}>
            <Scan />
          </Button>
        </GlassButtonWell>
      ) : null}
      {onToggleFullscreen ? (
        <GlassButtonWell>
          <Button
            type='button'
            variant='ghost'
            size='icon-lg'
            onClick={onToggleFullscreen}
            title={fullscreenLabel}
            aria-label={fullscreenLabel}>
            {isFullscreen ? <Minimize2 /> : <Maximize2 />}
          </Button>
        </GlassButtonWell>
      ) : null}
      {onToggleMinimap ? (
        <TooltipWrapper label={minimapLabel} side='left' offset={5}>
          <GlassButtonWell>
            <Button
              type='button'
              variant='ghost'
              size='icon-lg'
              disabled={disabled}
              pressed='accent'
              aria-pressed={isMinimapVisible}
              aria-label={minimapLabel}
              onClick={onToggleMinimap}>
              <Map />
            </Button>
          </GlassButtonWell>
        </TooltipWrapper>
      ) : null}
      {onTogglePhotoCounts ? (
        <TooltipWrapper label={photoCountsLabel} side='left' offset={5}>
          <GlassButtonWell>
            <Button
              type='button'
              variant='ghost'
              size='icon-lg'
              pressed='accent'
              aria-pressed={arePhotoCountsVisible}
              aria-label={photoCountsLabel}
              onClick={onTogglePhotoCounts}>
              {arePhotoCountsVisible ? <Eye /> : <EyeOff />}
            </Button>
          </GlassButtonWell>
        </TooltipWrapper>
      ) : null}
    </ZoomControlsContainer>
  )
}
